import { useEffect, useState } from "react";
import QRCode from "qrcode";

import { getPwaMode } from "../config/pwa";

const apps = [
  {
    mode: "global",
    name: "WellTech Global",
    audience: "Central e gestores",
    url: "https://app.welltech.net.br/instalar",
    shortUrl: "app.welltech.net.br",
    mark: "G",
    description: "Visão completa da operação: clientes, chamados, equipes, ativos, relatórios, aprovações e histórico centralizado.",
    features: ["Gestão de todos os clientes", "Acompanhamento e aprovações", "Indicadores e rastreabilidade"]
  },
  {
    mode: "campo",
    name: "WellTech Campo",
    audience: "Técnicos em atendimento",
    url: "https://campo.welltech.net.br/instalar",
    shortUrl: "campo.welltech.net.br",
    mark: "C",
    description: "Aplicativo de trabalho do técnico para consultar chamados, conversar com a central e registrar serviços e evidências em campo.",
    features: ["Ordens de serviço", "Fotos e diário de obra", "Interações pelo celular"]
  },
  {
    mode: "cliente",
    name: "WellTech Cliente",
    audience: "Clientes atendidos",
    url: "https://cliente.welltech.net.br/instalar",
    shortUrl: "cliente.welltech.net.br",
    mark: "CL",
    description: "Canal direto do cliente para abrir solicitações, conversar com a equipe e acompanhar protocolos e serviços realizados.",
    features: ["Abertura de solicitações", "Chat e acompanhamento", "Histórico de atendimentos"]
  }
];

const serviceAreas = [
  { code: "01", title: "Tecnologia em TI e Telecom", summary: "Soluções integradas para conectividade, comunicação, segurança e continuidade das operações.", services: [
    ["Ativos e gerenciamento de rede", "Instalação, configuração, gestão e monitoramento contínuo de ativos, com foco em desempenho, segurança e escalabilidade."],
    ["Projetos de Wi-Fi", "Planejamento de cobertura e capacidade, proteção contra acessos não autorizados e alta performance."],
    ["Telefonia analógica e IP", "Instalação, configuração e integração de sistemas analógicos e VoIP às redes IP."],
    ["CFTV analógico, IP e analíticos LPR", "Videomonitoramento, reconhecimento de placas e acesso remoto seguro às câmeras."],
    ["Som e comunicação IP", "Sistemas de áudio integrados por IP para ambientes comerciais, industriais e educacionais."],
    ["Controle de acesso", "Soluções faciais, biométricas e para estacionamentos."],
    ["Servidores ERP e Datacenter", "Planejamento, configuração, manutenção preventiva e corretiva de infraestrutura crítica."],
    ["Segurança da informação", "Proteção de dados sensíveis contra ameaças e apoio à conformidade regulatória."],
    ["SDAI — Detecção e alarme de incêndio", "Instalação de sistemas de detecção e alarme para proteção das instalações e das pessoas."]
  ]},
  { code: "02", title: "Infraestrutura Seca", summary: "Base física certificada para redes de dados, voz, imagem e automação.", services: [
    ["Cabeamento estruturado metálico e óptico", "Planejamento, instalação, organização e certificação da infraestrutura para TI e Telecom."]
  ]},
  { code: "03", title: "Energia", summary: "Projetos para eficiência, proteção, continuidade e sustentabilidade energética.", services: [
    ["Projetos em energia geral", "Consultoria, planejamento e implementação de soluções que otimizam recursos e garantem eficiência."],
    ["Sistemas de energia ininterrupta", "Dimensionamento e instalação de nobreaks para manter sistemas críticos operacionais."],
    ["Energia solar", "Implementação de sistemas fotovoltaicos para reduzir custos e contribuir com a sustentabilidade."]
  ]},
  { code: "04", title: "Telecom", summary: "Conectividade, suporte especializado e infraestrutura óptica de alta confiabilidade.", services: [
    ["Consultoria e suporte HelpDesk", "Atendimento técnico especializado 24/7 e contratos com SLA ajustado a cada cliente."],
    ["Projetos e reparos em fibra óptica", "Instalação e recuperação de redes ópticas para alta velocidade e disponibilidade."],
    ["Provedor de acesso à internet", "Conectividade de alta velocidade com segurança e suporte técnico dedicado."]
  ]}
];

export function PwaInstallPage() {
  const currentMode = getPwaMode();
  const [qrCodes, setQrCodes] = useState({});
  const [installState, setInstallState] = useState(window.wellInstallPrompt ? "ready" : "instructions");

  useEffect(() => {
    let active = true;
    Promise.all(apps.map(async (app) => [app.mode, await QRCode.toDataURL(app.url, {
      width: 360,
      margin: 2,
      errorCorrectionLevel: "M",
      color: { dark: "#123047", light: "#ffffff" }
    })])).then((entries) => active && setQrCodes(Object.fromEntries(entries)));

    const ready = () => setInstallState("ready");
    const installed = () => setInstallState("installed");
    window.addEventListener("well:install-ready", ready);
    window.addEventListener("well:app-installed", installed);
    return () => {
      active = false;
      window.removeEventListener("well:install-ready", ready);
      window.removeEventListener("well:app-installed", installed);
    };
  }, []);

  async function installCurrentApp() {
    if (!window.wellInstallPrompt) {
      setInstallState("instructions");
      document.getElementById("instalacao")?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    window.wellInstallPrompt.prompt();
    const choice = await window.wellInstallPrompt.userChoice;
    if (choice.outcome === "accepted") setInstallState("installed");
    window.wellInstallPrompt = null;
  }

  return (
    <main className="install-page">
      <nav className="institutional-nav" aria-label="Navegação institucional">
        <a className="institutional-logo" href="#inicio"><img src="/well-tecnologia-logo.png" alt="Well — Conectando Soluções" /></a>
        <div>
          <a href="#empresa">A empresa</a>
          <a href="#atuacao">Atuação</a>
          <a href="#aplicativos">Aplicativos</a>
          <a href="#instalacao">Como instalar</a>
        </div>
        <a className="institutional-nav-action" href="#aplicativos">Acessar soluções</a>
      </nav>
      <header className="install-hero">
        <div className="install-brand install-brand-official"><img src="/well-tecnologia-logo.png" alt="Well — Conectando Soluções" /></div>
        <div className="install-hero-copy">
          <span className="install-kicker">CENTRAL DE INSTALAÇÃO</span>
          <h1>Escolha o aplicativo para cada perfil</h1>
          <p>Escaneie o QR Code no celular ou abra o link correspondente. Os três aplicativos compartilham os mesmos protocolos e mantêm todo o histórico das interações.</p>
        </div>
        <div className="install-trace"><b>Uma demanda</b><span>Todos os canais</span><small>Rastreabilidade preservada</small></div>
      </header>

      <section className="institutional-intro" id="empresa">
        <div>
          <span className="install-kicker">SOBRE A WELLTECH</span>
          <h2>Tecnologia com contexto, controle e continuidade</h2>
        </div>
        <div>
          <p>A WellTech é especializada em fornecer soluções completas de TI, Telecom e Energia, com expertise em integração de sistemas para otimizar operações, ampliar a segurança e reduzir custos.</p>
          <p>Integramos tecnologia, infraestrutura e conhecimento técnico para conectar clientes, equipes de campo e gestão em uma memória operacional confiável.</p>
        </div>
      </section>

      <section className="institutional-pillars" aria-label="Missão, visão e valores">
        <article><span>01</span><small>MISSÃO</small><h3>Capacitar clientes com tecnologia de ponta</h3><p>Oferecer soluções tecnológicas e energéticas sustentáveis, garantindo segurança, eficiência e continuidade nos negócios.</p></article>
        <article><span>02</span><small>VISÃO</small><h3>Ser referência em inteligência operacional</h3><p>Evoluir continuamente uma plataforma capaz de preservar conhecimento e apoiar decisões em campo, na central e junto ao cliente.</p></article>
        <article><span>03</span><small>VALORES</small><h3>Confiança construída em cada interação</h3><p>Transparência, responsabilidade, segurança, colaboração, respeito ao cliente, excelência técnica e melhoria contínua.</p></article>
      </section>

      <section className="institutional-activities" id="atuacao">
        <div className="institutional-activities-copy">
          <span className="install-kicker">NOSSA ATUAÇÃO</span>
          <h2>Da solicitação ao histórico técnico completo</h2>
          <p>A plataforma WellTech acompanha a operação de ponta a ponta e mantém todos os envolvidos no mesmo fluxo de informação.</p>
          <div className="institutional-flow"><span>Cliente</span><i>→</i><span>Central</span><i>→</i><span>Técnico</span><i>→</i><span>Memória técnica</span></div>
        </div>
        <div className="institutional-differentials">
          <article><b>Integração total</b><p>TI, Telecom e Energia coordenadas para gerar eficiência e redução de custos.</p></article>
          <article><b>Equipe multidisciplinar</b><p>Conhecimento integrado para desafios de infraestrutura, tecnologia e energia.</p></article>
          <article><b>Compromisso sustentável</b><p>Soluções orientadas à eficiência e à redução do impacto ambiental.</p></article>
        </div>
      </section>

      <section className="services-catalog" aria-labelledby="services-title">
        <header><span className="install-kicker">SERVIÇOS OFERECIDOS</span><h2 id="services-title">Soluções completas para operações críticas</h2><p>Planejamento, implantação, integração, monitoramento e suporte em todo o ciclo de vida da infraestrutura.</p></header>
        <div className="service-area-list">
          {serviceAreas.map((area) => <article className="service-area" key={area.code}>
            <div className="service-area-heading"><span>{area.code}</span><div><h3>{area.title}</h3><p>{area.summary}</p></div></div>
            <div className="service-items">{area.services.map(([name, description]) => <div key={name}><b>{name}</b><p>{description}</p></div>)}</div>
          </article>)}
        </div>
      </section>

      <section className="install-section-heading" id="aplicativos">
        <span className="install-kicker">ECOSSISTEMA WELLTECH</span>
        <h2>Uma plataforma, três experiências conectadas</h2>
        <p>Cada pessoa acessa somente o que precisa, enquanto protocolos, mensagens e históricos permanecem integrados.</p>
      </section>
      <section className="install-app-grid" aria-label="Aplicativos WellTech">
        {apps.map((app) => {
          const isCurrent = app.mode === currentMode;
          return (
            <article className={`install-app-card ${app.mode} ${isCurrent ? "current" : ""}`} key={app.mode}>
              <div className="install-card-top">
                <span className="install-app-icon">{app.mark}</span>
                <div><small>{app.audience}</small><h2>{app.name}</h2></div>
                {isCurrent ? <b className="install-current-badge">Você está aqui</b> : null}
              </div>
              <p>{app.description}</p>
              <ul>{app.features.map((feature) => <li key={feature}>{feature}</li>)}</ul>
              <div className="install-qr-wrap">
                {qrCodes[app.mode] ? <img src={qrCodes[app.mode]} alt={`QR Code para instalar ${app.name}`} /> : <span>Gerando QR Code…</span>}
                <div><small>ACESSE PELO CELULAR</small><strong>{app.shortUrl}</strong><span>Aponte a câmera para o código</span></div>
              </div>
              {isCurrent ? (
                <button className="install-primary" type="button" onClick={installCurrentApp}>
                  {installState === "installed" ? "Aplicativo instalado" : "Instalar neste dispositivo"}
                </button>
              ) : <a className="install-secondary" href={app.url}>Abrir página de instalação</a>}
            </article>
          );
        })}
      </section>

      <section className="install-help" id="instalacao">
        <div><span className="install-kicker">COMO INSTALAR</span><h2>Leva menos de um minuto</h2><p>Não é necessário baixar APK nem acessar uma loja de aplicativos.</p></div>
        <ol>
          <li><b>Android</b><span>Abra no Chrome, toque em “Instalar aplicativo” ou no menu ⋮ e depois em “Adicionar à tela inicial”.</span></li>
          <li><b>iPhone e iPad</b><span>Abra no Safari, toque em Compartilhar e escolha “Adicionar à Tela de Início”.</span></li>
          <li><b>Computador</b><span>Abra no Chrome ou Edge e clique no ícone de instalação exibido na barra de endereço.</span></li>
        </ol>
      </section>

      <footer className="install-footer"><strong>WellTech</strong><span>Inteligência que conecta operações</span><small>Versão 9.1 · Instalação segura via navegador</small></footer>
    </main>
  );
}
